import {
  ActionIcon,
  Box,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core"
import {
  ArrowDownIcon,
  ArrowsCounterClockwiseIcon,
  ArrowUpIcon,
  DotsSixVerticalIcon,
  LightbulbIcon,
  RobotIcon,
  XIcon,
} from "@phosphor-icons/react"
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd"
import { useTranslation } from "react-i18next"
import { WizardSection } from "./WizardSection"
import { PriorityBadge } from "./wizard-badges"
import type { DnRow } from "./types"
import { renderBusinessName } from "../../../utils/render-business-name"

interface Props {
  stops         : DnRow[]   // ordered
  onMove        : (idx: number, dir: -1 | 1) => void
  onReorder     : (fromIdx: number, toIdx: number) => void
  onRemove      : (id: string) => void
  onAutoOptimize: () => void
  onReset       : () => void
  optimizing    : boolean
}

export function StopSequenceSection({
  stops,
  onMove,
  onReorder,
  onRemove,
  onAutoOptimize,
  onReset,
  optimizing,
}: Props) {
  const { t } = useTranslation(["delivery"])
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    if (result.destination.index === result.source.index) return
    onReorder(result.source.index, result.destination.index)
  }

  return (
    <WizardSection
      n={3}
      title={t("delivery:route.wizardUi.sequence.title")}
      helpKey="order.delivery-routes-new.section.order"
      helpId="sec-order"
      aside={
        <Group gap="xs">
          <Tooltip
            label={t("delivery:route.wizardUi.sequence.autoNote")}
            withArrow
            position="top"
          >
            <Button
              size="sm"
              variant="default"
              leftSection={<RobotIcon size={16} />}
              onClick={onAutoOptimize}
              loading={optimizing}
              disabled={stops.length < 2}
            >
              {t("delivery:route.wizardUi.sequence.sortByPriority")}
            </Button>
          </Tooltip>
          <Button
            size="sm"
            variant="default"
            leftSection={<ArrowsCounterClockwiseIcon size={16} />}
            onClick={onReset}
            disabled={stops.length === 0}
          >
            {t("delivery:route.wizardUi.sequence.reset")}
          </Button>
        </Group>
      }
    >
        {stops.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            {t("delivery:route.wizardUi.sequence.empty")}
          </Text>
        ) : (
          <Stack gap="md">
            <Group gap={6} wrap="nowrap" align="flex-start">
              <LightbulbIcon size={14} weight="fill" color="var(--mantine-color-yellow-6)" style={{ flexShrink: 0, marginTop: 2 }} />
              <Text size="sm" c="dimmed">
                {t("delivery:route.wizardUi.sequence.hint")}
              </Text>
            </Group>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="stops">
                {(droppable) => (
                  <Paper
                    withBorder
                    radius="sm"
                    ref={droppable.innerRef}
                    {...droppable.droppableProps}
                  >
                    <Stack gap={0}>
                      {stops.map((dn, idx) => {
                        const id           = dn.id ?? ""
                        const customerName =
                          renderBusinessName(dn.receiverBusiness, "") ||
                          renderBusinessName(dn.receiverBusiness?.partner, "-")
                        return (
                          <Draggable key={id} draggableId={id} index={idx}>
                            {(draggable, snapshot) => (
                              <Group
                                ref={draggable.innerRef}
                                {...draggable.draggableProps}
                                gap="sm"
                                wrap="nowrap"
                                px="md"
                                py="sm"
                                style={{
                                  borderBottom: "1px solid var(--mantine-color-gray-1)",
                                  background  : snapshot.isDragging ? "var(--mantine-color-brand-0)" : undefined,
                                  ...draggable.draggableProps.style,
                                }}
                              >
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  color="gray"
                                  style={{ cursor: "grab" }}
                                  {...draggable.dragHandleProps}
                                  aria-label={t("delivery:route.wizardUi.sequence.drag")}
                                >
                                  <DotsSixVerticalIcon size={14} />
                                </ActionIcon>
                                <ThemeIcon size={28} radius="md" variant="light" color="gray">
                                  <Text size="sm" fw={700}>{idx + 1}</Text>
                                </ThemeIcon>
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                  <Group gap={6} wrap="nowrap">
                                    <Text size="sm" fw={600} truncate>{customerName}</Text>
                                    <PriorityBadge priority={dn.sla?.priority} />
                                  </Group>
                                  <Text size="sm" c="dimmed" mt={2} truncate>
                                    {dn.refCode || "-"}
                                    {dn.classification?.deliveryChannel === "LOCAL_DELIVERY" && ` · ${t("delivery:route.wizardUi.channel.LOCAL_DELIVERY")}`}
                                    {dn.classification?.deliveryChannel === "SUPPLY_FREIGHT" && ` · ${t("delivery:route.wizardUi.channel.SUPPLY_FREIGHT")}`}
                                    {dn.totalItemCount ? ` · ${t("delivery:route.wizardUi.pieces", { count: dn.totalItemCount })}` : ""}
                                  </Text>
                                </Box>
                                <Group gap={4}>
                                  <ActionIcon
                                    size="sm"
                                    variant="default"
                                    disabled={idx === 0}
                                    onClick={() => onMove(idx, -1)}
                                    aria-label={t("delivery:route.wizardUi.sequence.up")}
                                  >
                                    <ArrowUpIcon size={16} />
                                  </ActionIcon>
                                  <ActionIcon
                                    size="sm"
                                    variant="default"
                                    disabled={idx === stops.length - 1}
                                    onClick={() => onMove(idx, 1)}
                                    aria-label={t("delivery:route.wizardUi.sequence.down")}
                                  >
                                    <ArrowDownIcon size={16} />
                                  </ActionIcon>
                                  <ActionIcon
                                    size="sm"
                                    variant="default"
                                    color="red"
                                    onClick={() => onRemove(id)}
                                    aria-label={t("delivery:route.wizardUi.sequence.remove")}
                                  >
                                    <XIcon size={16} />
                                  </ActionIcon>
                                </Group>
                              </Group>
                            )}
                          </Draggable>
                        )
                      })}
                      {droppable.placeholder}
                    </Stack>
                  </Paper>
                )}
              </Droppable>
            </DragDropContext>
          </Stack>
        )}
    </WizardSection>
  )
}
